/**
 * Money handling utilities for Dnceclub
 *
 * IMPORTANT: Backend API always returns DOLLARS to the frontend.
 * Use formatCurrency() for all backend API responses.
 *
 * The only exception is Stripe data (paymentIntent.amount) which is in CENTS.
 * Use formatCentsAsCurrency() only for direct Stripe API responses.
 *
 * WARNING THRESHOLDS (in development mode):
 * - formatCurrency() warns if value > 10000 (might be cents passed as dollars)
 * - formatCentsAsCurrency() warns if value < 100 (might be dollars passed as cents)
 */

// Import from shared package - these are the canonical implementations
import {
  centsToDollars as sharedCentsToDollars,
  dollarsToCents as sharedDollarsToCents,
} from "@dnceclub/shared";

// Re-export for external consumers
export const centsToDollars = sharedCentsToDollars;
export const dollarsToCents = sharedDollarsToCents;

// Warning thresholds to detect likely unit mismatches
const DOLLAR_WARNING_THRESHOLD = 10000; // Values > $10,000 might be cents
const CENTS_WARNING_THRESHOLD = 100; // Values < $1.00 in cents might be dollars

function warnIfSuspiciousDollarValue(amount: number, context?: string): void {
  if (process.env.NODE_ENV === 'development' && amount > DOLLAR_WARNING_THRESHOLD) {
    console.warn(
      `[money-utils] Suspicious dollar value: ${amount}. ` +
      `This might be cents passed to formatCurrency(). ` +
      `Expected dollars, got value > ${DOLLAR_WARNING_THRESHOLD}.` +
      (context ? ` Context: ${context}` : '')
    );
  }
}

function warnIfSuspiciousCentsValue(cents: number, context?: string): void {
  if (process.env.NODE_ENV === 'development' && cents > 0 && cents < CENTS_WARNING_THRESHOLD) {
    console.warn(
      `[money-utils] Suspicious cents value: ${cents}. ` +
      `This might be dollars passed to formatCentsAsCurrency(). ` +
      `Expected cents, got value < ${CENTS_WARNING_THRESHOLD}.` +
      (context ? ` Context: ${context}` : '')
    );
  }
}

/**
 * Formats a dollar amount for display
 * @param amount - The amount in dollars (e.g., 100 for $100.00)
 * @param context - Optional context for debugging warnings
 * @returns Formatted string with dollar sign and two decimal places
 */
export function formatCurrency(amount: number | undefined | null, context?: string): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return "$0.00";
  }

  warnIfSuspiciousDollarValue(amount, context);
  return `$${amount.toFixed(2)}`;
}

/**
 * Formats a cents amount for display as currency
 * @param cents - The amount in cents (e.g., 10000 for $100.00)
 * @param context - Optional context for debugging warnings
 * @returns Formatted string with dollar sign and two decimal places
 */
export function formatCentsAsCurrency(cents: number | undefined | null, context?: string): string {
  if (cents === undefined || cents === null || isNaN(cents)) {
    return "$0.00";
  }

  warnIfSuspiciousCentsValue(cents, context);
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}`;
}

/**
 * Formats a dollar amount for display, omitting decimal places if they are .00
 * @param amount - The amount in dollars (e.g., 100 for $100, 100.50 for $100.50)
 * @returns Formatted string with dollar sign, decimal places only if needed
 */
export function formatCurrencyCompact(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return "$0";
  }

  // Check if the amount is a whole number
  if (Number.isInteger(amount)) {
    return `$${amount}`;
  }

  return `$${amount.toFixed(2)}`;
}

/**
 * Calculates the total price for multiple hours
 * @param hourlyRate - The hourly rate in dollars
 * @param hours - Number of hours
 * @returns Total price in dollars
 */
export function calculateTotalPrice(hourlyRate: number, hours: number): number {
  return hourlyRate * hours;
}

/**
 * Parses a currency string to a number
 * @param value - String value like "$100.00" or "100.00"
 * @returns Number value in dollars
 */
export function parseCurrency(value: string): number {
  // Remove dollar sign and any non-numeric characters except decimal point
  const cleanValue = value.replace(/[^0-9.-]/g, "");
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Validates if a price is valid (non-negative and reasonable)
 * @param price - Price in dollars
 * @returns boolean indicating if price is valid
 */
export function isValidPrice(price: number): boolean {
  return price >= 0 && price <= 10000; // Max $10,000 per hour seems reasonable
}

/**
 * Parses user input (like "$100.00" or "100.00") to cents
 * @param dollarString - String value representing dollars
 * @returns Number value in cents
 */
export function parseInputToCents(dollarString: string): number {
  const dollars = parseCurrency(dollarString);
  return Math.round(dollars * 100);
}

/**
 * Gets the display value in dollars based on whether cents or dollars are used
 * @param dollarValue - The value in dollars (legacy format)
 * @param centsValue - The value in cents (new format)
 * @param useCents - Whether to use the cents value
 * @returns Number in dollars for display purposes
 */
export function getMoneyDisplayValue(
  dollarValue: number | null | undefined,
  centsValue: number | null | undefined,
  useCents: boolean
): number {
  if (useCents) {
    return centsToDollars(centsValue);
  }
  return dollarValue ?? 0;
}
